import type { FlowConfigViewModel } from "@models/doctype";

export type FieldChangeKind = "added" | "removed" | "changed";

export interface FieldChange {
  /** Readable location, e.g. `Step 2 › Agent engines › userPrompt`. */
  path: string;
  kind: FieldChangeKind;
  before?: string;
  after?: string;
}

export interface LineDiff {
  type: "same" | "add" | "del";
  text: string;
}

/** Bookkeeping that differs between any two versions and says nothing about behaviour. */
const IGNORED = new Set(["id", "version", "isActive", "createdAt", "updatedAt", "updatedBy", "flowBaseId", "docTypeId"]);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date);

const render = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value, null, 2);

/**
 * Flatten a flow config to `path → leaf`. Steps are keyed by their order and
 * agents by their domain key, so reordering an array does not show up as every
 * field changing. Response schemas stay whole: a schema is read as one document.
 */
const flatten = (value: unknown, path: string, out: Map<string, unknown>) => {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) {
    if (path.endsWith("steps")) {
      for (const step of value as Array<{ stepOrder: number; stepType: string }>) {
        flatten(step, `Step ${step.stepOrder} (${step.stepType})`, out);
      }
      return;
    }
    if (path.endsWith("executions")) {
      const prefix = path.slice(0, -"executions".length);
      for (const agent of value as Array<{ domainKey: string }>) {
        flatten(agent, `${prefix}Agent ${agent.domainKey}`, out);
      }
      return;
    }
    out.set(path, value);
    return;
  }
  if (isPlainObject(value) && !path.endsWith("responseSchema")) {
    for (const [key, child] of Object.entries(value)) {
      if (IGNORED.has(key) || key === "stepOrder" || key === "domainKey") continue;
      const childPath = key === "llmStepConfig" || key === "ocrStepConfig" ? path : path ? `${path} › ${key}` : key;
      flatten(child, key === "executions" ? `${path} › executions` : childPath, out);
    }
    return;
  }
  out.set(path, value);
};

const flat = (config: FlowConfigViewModel) => {
  const out = new Map<string, unknown>();
  flatten(config, "", out);
  return out;
};

/** Every behavioural field that differs between `base` (e.g. the live version) and `next`. */
export const diffFlowConfigs = (base: FlowConfigViewModel, next: FlowConfigViewModel): FieldChange[] => {
  const before = flat(base);
  const after = flat(next);
  const changes: FieldChange[] = [];
  for (const [path, value] of before) {
    if (!after.has(path)) {
      changes.push({ path, kind: "removed", before: render(value) });
    } else if (render(value) !== render(after.get(path))) {
      changes.push({ path, kind: "changed", before: render(value), after: render(after.get(path)) });
    }
  }
  for (const [path, value] of after) {
    if (!before.has(path)) changes.push({ path, kind: "added", after: render(value) });
  }
  return changes;
};

/** Above this many line pairs the LCS table is too big to build in the browser. */
const MAX_CELLS = 4_000_000;

/** Line diff of two texts (longest common subsequence); null when too large to compute. */
export const diffLines = (before: string, after: string): LineDiff[] | null => {
  const a = before.split("\n");
  const b = after.split("\n");
  if ((a.length + 1) * (b.length + 1) > MAX_CELLS) return null;
  const width = b.length + 1;
  const lcs = new Uint32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i * width + j] = a[i] === b[j]
        ? lcs[(i + 1) * width + j + 1] + 1
        : Math.max(lcs[(i + 1) * width + j], lcs[i * width + j + 1]);
    }
  }
  const out: LineDiff[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ type: "same", text: a[i] });
      i++;
      j++;
    } else if (lcs[(i + 1) * width + j] >= lcs[i * width + j + 1]) {
      out.push({ type: "del", text: a[i++] });
    } else {
      out.push({ type: "add", text: b[j++] });
    }
  }
  while (i < a.length) out.push({ type: "del", text: a[i++] });
  while (j < b.length) out.push({ type: "add", text: b[j++] });
  return out;
};
